import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  RefreshControl,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { ClubsStackParamList } from '../../navigation/ClubsNavigator';
import { ThemedText } from '../../components/common/ThemedText';
import { useTheme } from '../../theme/ThemeContext';
import { getMeetups, acceptMeetup, rejectMeetup, Meetup } from '../../lib/api/meetups';

type MeetupsListNavigationProp = StackNavigationProp<ClubsStackParamList, 'MeetupsList'>;
type MeetupsListRouteProp = RouteProp<ClubsStackParamList, 'MeetupsList'>;

export const MeetupsListScreen: React.FC = () => {
  const navigation = useNavigation<MeetupsListNavigationProp>();
  const route = useRoute<MeetupsListRouteProp>();
  const { clubId } = route.params;
  const { theme } = useTheme();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sent, setSent] = useState<Meetup[]>([]);
  const [received, setReceived] = useState<Meetup[]>([]);
  const [activeTab, setActiveTab] = useState<'sent' | 'received'>('received');

  useEffect(() => {
    loadMeetups();
  }, []);

  const loadMeetups = async () => {
    try {
      setLoading(true);
      const data = await getMeetups(clubId);
      setSent(data.sent);
      setReceived(data.received);
    } catch (error) {
      console.error('Error loading meetups:', error);
      Alert.alert('Error', 'Failed to load meetups');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadMeetups();
  };

  const handleAccept = async (id: string) => {
    try {
      await acceptMeetup(id);
      Alert.alert('Success', 'Meetup accepted');
      loadMeetups();
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to accept meetup');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectMeetup(id);
      Alert.alert('Success', 'Meetup rejected');
      loadMeetups();
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to reject meetup');
    }
  };

  const renderMeetup = (meetup: Meetup, isSent: boolean) => {
    const otherUser = isSent ? meetup.recipient : meetup.creator;
    const isPending = meetup.status === 'pending';
    const isAccepted = meetup.status === 'accepted';
    const isRejected = meetup.status === 'rejected';

    return (
      <View
        key={meetup.id}
        style={[styles.meetupCard, { backgroundColor: theme.card, borderColor: theme.border }]}
      >
        {/* User Info */}
        <View style={styles.meetupHeader}>
          <View style={[styles.avatar, { backgroundColor: theme.border }]}>
            <Ionicons name="person" size={24} color={theme.textSecondary} />
          </View>
          <View style={styles.meetupInfo}>
            <ThemedText style={[styles.userName, { color: theme.text }]}>
              {otherUser.name || 'Unknown User'}
            </ThemedText>
            {otherUser.profession && (
              <ThemedText style={[styles.userProfession, { color: theme.textSecondary }]}>
                {otherUser.profession}
              </ThemedText>
            )}
          </View>
          {/* Status Badge */}
          <View
            style={[
              styles.statusBadge,
              {
                backgroundColor: isPending
                  ? theme.accent + '20'
                  : isAccepted
                  ? '#10B981' + '20'
                  : '#EF4444' + '20',
              },
            ]}
          >
            <ThemedText
              style={[
                styles.statusText,
                {
                  color: isPending ? theme.accent : isAccepted ? '#10B981' : '#EF4444',
                },
              ]}
            >
              {meetup.status.charAt(0).toUpperCase() + meetup.status.slice(1)}
            </ThemedText>
          </View>
        </View>

        {/* Meetup Details */}
        <View style={styles.detailsContainer}>
          <View style={styles.detailRow}>
            <Ionicons name="business" size={16} color={theme.textSecondary} />
            <ThemedText style={[styles.detailText, { color: theme.text }]}>
              {meetup.place}
            </ThemedText>
          </View>
          <View style={styles.detailRow}>
            <Ionicons name="call" size={16} color={theme.textSecondary} />
            <ThemedText style={[styles.detailText, { color: theme.text }]}>
              {meetup.phone}
            </ThemedText>
          </View>
          <View style={styles.detailRow}>
            <Ionicons name="people" size={16} color={theme.textSecondary} />
            <ThemedText style={[styles.detailText, { color: theme.text }]}>
              {meetup.type}
            </ThemedText>
          </View>
          {meetup.club && (
            <View style={styles.detailRow}>
              <Ionicons name="home" size={16} color={theme.textSecondary} />
              <ThemedText style={[styles.detailText, { color: theme.text }]}>
                {meetup.club.name}
              </ThemedText>
            </View>
          )}
        </View>

        {/* Message */}
        {meetup.message && (
          <View style={[styles.messageContainer, { backgroundColor: theme.background }]}>
            <ThemedText style={[styles.message, { color: theme.textSecondary }]}>
              {meetup.message}
            </ThemedText>
          </View>
        )}

        {/* Action Buttons (for received pending meetups) */}
        {!isSent && isPending && (
          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: '#10B981' }]}
              onPress={() => handleAccept(meetup.id)}
              activeOpacity={0.7}
            >
              <Ionicons name="checkmark" size={20} color="#FFFFFF" />
              <ThemedText style={styles.actionButtonText}>Accept</ThemedText>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: '#EF4444' }]}
              onPress={() => handleReject(meetup.id)}
              activeOpacity={0.7}
            >
              <Ionicons name="close" size={20} color="#FFFFFF" />
              <ThemedText style={styles.actionButtonText}>Reject</ThemedText>
            </TouchableOpacity>
          </View>
        )}

        {/* Timestamp */}
        <ThemedText style={[styles.timestamp, { color: theme.textSecondary }]}>
          {new Date(meetup.createdAt).toLocaleDateString()}
        </ThemedText>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={[styles.header, { borderBottomColor: theme.border }]}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color={theme.text} />
          </TouchableOpacity>
          <ThemedText style={styles.headerTitle}>Meetup Requests</ThemedText>
          <View style={{ width: 24 }} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </View>
    );
  }

  const activeMeetups = activeTab === 'sent' ? sent : received;

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={styles.headerTitle}>Meetup Requests</ThemedText>
        <View style={{ width: 24 }} />
      </View>

      {/* Tabs */}
      <View style={[styles.tabs, { borderBottomColor: theme.border }]}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'received' && { borderBottomColor: theme.accent }]}
          onPress={() => setActiveTab('received')}
        >
          <ThemedText
            style={[
              styles.tabText,
              {
                color: activeTab === 'received' ? theme.accent : theme.textSecondary,
              },
            ]}
          >
            Received ({received.length})
          </ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'sent' && { borderBottomColor: theme.accent }]}
          onPress={() => setActiveTab('sent')}
        >
          <ThemedText
            style={[
              styles.tabText,
              {
                color: activeTab === 'sent' ? theme.accent : theme.textSecondary,
              },
            ]}
          >
            Sent ({sent.length})
          </ThemedText>
        </TouchableOpacity>
      </View>

      {/* Meetups List */}
      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={theme.accent} />
        }
      >
        {activeMeetups.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="calendar-outline" size={64} color={theme.textSecondary} />
            <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
              No {activeTab} meetup requests
            </ThemedText>
          </View>
        ) : (
          activeMeetups.map((meetup) => renderMeetup(meetup, activeTab === 'sent'))
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingTop: 48,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabText: {
    fontSize: 16,
    fontWeight: '500',
  },
  content: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    marginTop: 16,
  },
  meetupCard: {
    marginHorizontal: 16,
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  meetupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  meetupInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 16,
    fontWeight: '600',
  },
  userProfession: {
    fontSize: 14,
    marginTop: 2,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  detailsContainer: {
    gap: 8,
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailText: {
    fontSize: 14,
  },
  messageContainer: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  message: {
    fontSize: 14,
    lineHeight: 20,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
    gap: 6,
  },
  actionButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  timestamp: {
    fontSize: 12,
    textAlign: 'right',
  },
});
